import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  memo,
  useTransition,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  LoadingContext,
  M_StateActions,
  MHeader,
  MButton,
  NavigateContext,
} from "src/M_Components";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import DynamicForm from "src/components/Forms/DynamicForm";
import EsriGenericMap from "src/components/Maps/EsriGenericMap";
import "../pages.css";

/* =========================================================================================================================
 * LocationConfigCrud renders a CRUD screen for Location entities, consisting of:
 * - A dynamic form driven by server-provided schema/options
 * - An Esri-based map that supports picking coordinates and reverse geocoding
 * - Per-tab state persistence to restore user progress when switching tabs
 *
 * The component coordinates multiple asynchronous flows:
 * - Loading form schema/options
 * - Loading entity details for view/edit
 * - Persisting per-tab snapshots to Redux state
 * - Driving map center/zoom and marker layers via props
 * ======================================================================================================================= */

/* =========================================================================================================================
 * toCamel
 *
 * Converts a string to lower camelCase by lowercasing the first character.
 * Used to map schema field names into payload property names expected by the API.
 * ======================================================================================================================= */
/**
 * Converts a string to lower camelCase.
 * @param {string | null | undefined} s - Input string.
 * @returns {string | null | undefined} Lower camelCase string, or the original falsy value.
 */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/* =========================================================================================================================
 * isBoolean
 *
 * Determines whether a schema option represents a boolean field.
 * ======================================================================================================================= */
/**
 * Checks whether a schema option represents a boolean field.
 * @param {object} opt - Schema option item.
 * @returns {boolean} True when the option type is boolean.
 */
const isBoolean = (opt) => opt?.Type === "boolean" || opt?.Type === "bool";

/* =========================================================================================================================
 * deriveId
 *
 * Resolves the most appropriate entity Id from multiple sources:
 * - the currently selected row (line)
 * - the fetched entity payload (info)
 * - the current local state (current)
 * - a stored snapshot value (snap)
 *
 * This allows the UI to function consistently across:
 * - create mode (no Id yet)
 * - edit/view mode (Id exists)
 * - tab switches and restored sessions
 * ======================================================================================================================= */
/**
 * Resolves an entity Id from multiple sources in order of priority.
 * @param {object | null | undefined} line - Row data that may contain an Id.
 * @param {object | null | undefined} info - Loaded entity data that may contain an Id.
 * @param {number | string | null | undefined} current - Current local Id state.
 * @param {number | string | null | undefined} snap - Snapshot Id from persisted tab state.
 * @returns {number | string | null} Resolved Id or null when unavailable.
 */
const deriveId = (line, info, current, snap) =>
  (line && line.Id) != null
    ? line.Id
    : (info && info.Id) != null
      ? info.Id
      : snap != null
        ? snap
        : current != null
          ? current
          : null;

/* =========================================================================================================================
 * isFiniteNumber
 *
 * Validates that a value is a finite JavaScript number.
 * Used for Latitude/Longitude processing and map interactions.
 * ======================================================================================================================= */
/**
 * Checks whether a value is a finite number.
 * @param {unknown} n - Value to validate.
 * @returns {boolean} True when the value is a finite number.
 */
const isFiniteNumber = (n) => typeof n === "number" && Number.isFinite(n);

/* =========================================================================================================================
 * toNumberOrNull
 *
 * Normalizes common input values into a number or null:
 * - null/undefined/empty string => null
 * - numeric strings => number
 * - invalid numeric strings => null
 * ======================================================================================================================= */
/**
 * Converts an arbitrary value into a finite number or null.
 * @param {unknown} v - Value to convert.
 * @returns {number | null} Finite number or null.
 */
const toNumberOrNull = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* =========================================================================================================================
 * round6
 *
 * Rounds a finite number to 6 decimal places.
 * Used for coordinate normalization to keep stable map updates and payload values.
 * ======================================================================================================================= */
/**
 * Rounds a number to 6 decimal places.
 * @param {number} n - Input number.
 * @returns {number} Rounded number, or the original value when not finite.
 */
const round6 = (n) => {
  if (!Number.isFinite(n)) return n;
  return Math.round(n * 1_000_000) / 1_000_000;
};

/* =========================================================================================================================
 * pickFirstString
 *
 * Returns the first non-empty string produced from the provided candidates.
 * Used to robustly extract values from reverse-geocode candidate objects that may vary in shape.
 * ======================================================================================================================= */
/**
 * Picks the first non-empty string from a list of candidates.
 * @param  {...unknown} candidates - Candidate values.
 * @returns {string} First non-empty trimmed string or an empty string.
 */
const pickFirstString = (...candidates) => {
  for (const c of candidates) {
    const v = String(c ?? "").trim();
    if (v) return v;
  }
  return "";
};

/* =========================================================================================================================
 * shallowEqualPatch
 *
 * Checks whether a patch object would change any of the corresponding properties in a base object.
 * Used to avoid unnecessary state updates and re-renders when applying patches to the form/state.
 * ======================================================================================================================= */
/**
 * Checks whether all properties in patch match the values in base.
 * @param {object | null | undefined} base - Base object.
 * @param {object | null | undefined} patch - Patch object containing a subset of keys.
 * @returns {boolean} True when patch does not introduce differences.
 */
const shallowEqualPatch = (base, patch) => {
  if (!patch || typeof patch !== "object") return true;
  for (const k of Object.keys(patch)) {
    if ((base || {})[k] !== patch[k]) return false;
  }
  return true;
};

/* =========================================================================================================================
 * Map marker symbol constants
 *
 * These constants define the marker appearance used to represent the picked location on the map.
 * The symbol uses a path-based marker to match the desired pin shape.
 * ======================================================================================================================= */

/* Marker SVG path used by the ArcGIS "simple-marker" symbol with style "path". */
const PIN_PATH =
  "M16 0C9.4 0 4 5.4 4 12c0 9.4 12 20 12 20s12-10.6 12-20C28 5.4 22.6 0 16 0zM16 17.2c-2.9 0-5.2-2.3-5.2-5.2S13.1 6.8 16 6.8s5.2 2.3 5.2 5.2-2.3 5.2-5.2 5.2z";

/* Fill and outline colors for the pin symbol (ArcGIS RGBA arrays). */
const PIN_FILL = [0, 0, 0, 255];
const PIN_OUTLINE = [255, 255, 255, 255];

/* =========================================================================================================================
 * MapPanel
 *
 * MapPanel provides a thin layout wrapper around EsriGenericMap.
 *
 * Map lifetime requirements:
 * - The underlying map element must not be remounted by React using a `key`.
 * - Remounting can cause ArcGIS canvas/context timing issues and cancelled async work.
 *
 * All map updates are driven through props (center, zoom, layers, and callbacks) and handled by EsriGenericMap.
 * ======================================================================================================================= */
/**
 * Lightweight wrapper around EsriGenericMap that provides consistent sizing and styling.
 * @param {object} props - Component props.
 * @returns {JSX.Element} Map container with EsriGenericMap inside.
 */
const MapPanel = memo(function MapPanel({
  mode,
  locale,
  widgets,
  center,
  zoom,
  constraints,
  onComponentReady,
  onMapClick,
  onReverseGeocode,
  layers,
  showLocations,
  locations,
  enableLocationPick,
}) {
  return (
    <div
      style={{
        height: "clamp(1100px, 45vh, 1100px)",
        minHeight: 260,
        width: "100%",
        border: "2px solid var(--color-red-dark)",
        borderRadius: 8,
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
        contain: "layout paint size",
      }}
    >
      <EsriGenericMap
        mode={mode}
        locale={locale}
        widgets={widgets}
        layers={layers}
        center={center}
        zoom={zoom}
        constraints={constraints}
        onComponentReady={onComponentReady}
        onMapClick={onMapClick}
        onReverseGeocode={onReverseGeocode}
        showLocations={!!showLocations}
        locations={Array.isArray(locations) ? locations : []}
        enableLocationPick={!!enableLocationPick}
        configDebounceMs={180}
      />
    </div>
  );
});

/* =========================================================================================================================
 * LocationConfigCrud
 *
 * Screen component for creating, viewing, and editing Location entities.
 *
 * Responsibilities:
 * - Load schema/options and entity details
 * - Maintain per-tab state so each open tab restores its in-progress form/map state
 * - Submit create/update payloads based on the dynamic schema
 * - Drive map marker/center/zoom based on current coordinates
 * - Apply reverse-geocode results to form fields with user-edit protection
 * ======================================================================================================================= */
/**
 * CRUD component for Location configuration.
 * @param {object} props - Component props.
 * @param {Function} props.handleTabClose - Callback to close the current tab.
 * @param {Function} props.toastMessages - Callback to display toast notifications.
 * @returns {JSX.Element} Location CRUD view with form and map.
 */
export default function LocationConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  /* =====================================================================================================================
   * Transitions and loader coordination
   *
   * startUiTransition batches state updates into a React transition to keep input responsiveness.
   * loadingTick and bootstrapTick are used to trigger re-evaluation of derived loader states.
   * =================================================================================================================== */

  const [isPendingUi, startUiTransition] = useTransition();
  const [loadingTick, setLoadingTick] = useState(0);
  const bumpLoadingTick = useCallback(() => setLoadingTick((x) => x + 1), []);

  /* =====================================================================================================================
   * Global tab context values
   *
   * selectedTab identifies the active tab key.
   * globalOperation/globalLine are one-time values typically set by navigation when opening the tab.
   * =================================================================================================================== */

  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );

  const globalOperation = M_State.find((obj) => obj.key === "operation")?.value;
  const globalLine = M_State.find((obj) => obj.key === "LocationLine")?.value;

  /* =====================================================================================================================
   * Per-tab state
   *
   * id: current tab id used to locate per-tab snapshots in Redux
   * operation/sessionLine: mode and row context for this tab
   * locationInfo/locationId: current entity details and Id
   * allOptions: dynamic schema/options used to render the form and build payloads
   * fkInitialQueries: initial search strings for foreign key dropdowns
   * formRef: imperative API from DynamicForm (submit/getValues/reset)
   * =================================================================================================================== */

  const [id, setId] = useState(
    M_State.find((obj) => obj.key === "selectedTab")?.value
  );

  const [operation, setOperation] = useState(globalOperation ?? "Error");
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});
  const [locationInfo, setLocationInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);
  const [locationId, setLocationId] = useState(sessionLine?.Id);
  const [allOptions, setAllOptions] = useState([]);
  const [fkInitialQueries, setFkInitialQueries] = useState({});
  const formRef = useRef(null);

  /* =====================================================================================================================
   * Ref mirrors
   *
   * Refs store the latest values to be used safely inside async callbacks and timeouts.
   * This prevents stale closure issues when requests complete after state changes.
   * =================================================================================================================== */

  const idRef = useRef(id);
  const operationRef = useRef(operation);
  const locationIdRef = useRef(locationId);
  const sessionLineRef = useRef(sessionLine);
  const locationInfoRef = useRef(locationInfo);

  useEffect(() => {
    idRef.current = id;
  }, [id]);
  useEffect(() => {
    operationRef.current = operation;
  }, [operation]);
  useEffect(() => {
    locationIdRef.current = locationId;
  }, [locationId]);
  useEffect(() => {
    sessionLineRef.current = sessionLine;
  }, [sessionLine]);
  useEffect(() => {
    locationInfoRef.current = locationInfo;
  }, [locationInfo]);

  /* =====================================================================================================================
   * Auto-fill tracking for reverse geocoding
   *
   * These refs track:
   * - last auto-filled values
   * - whether the user has manually edited each field
   * - the last coordinates used for an auto-fill pass
   *
   * This ensures that reverse-geocode auto-fill does not overwrite user edits.
   * =================================================================================================================== */

  const lastAutoNameRef = useRef("");
  const lastAutoAddressRef = useRef("");
  const lastAutoCodeRef = useRef("");
  const lastAutoPlaceTypeIdRef = useRef(null);
  const lastAutoCountryIdRef = useRef(null);

  const userEditedNameRef = useRef(false);
  const userEditedAddressRef = useRef(false);
  const userEditedCodeRef = useRef(false);
  const userEditedPlaceTypeRef = useRef(false);
  const userEditedCountryRef = useRef(false);

  const lastCoordsForAutoRef = useRef(null); // { lat, lon }
  const forceOverwriteNextAutoRef = useRef(false);

  /* =====================================================================================================================
   * Tab token
   *
   * tabTokenRef increments on tab id changes.
   * Async operations compare their captured token against the current token
   * to avoid applying results to a different tab session.
   * =================================================================================================================== */

  const tabTokenRef = useRef(0);
  useEffect(() => {
    tabTokenRef.current += 1;
  }, [id]);

  /* =====================================================================================================================
   * Backend services
   *
   * BufferService instances are memoized by DEFAULTURL to avoid recreating clients on every render.
   * =================================================================================================================== */

  const svcLocations = useMemo(
    () => new BufferService("Locations", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcCreate = useMemo(
    () => new BufferService("Locations/Create", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcUpdate = useMemo(
    () => new BufferService("Locations/Update", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcCountries = useMemo(
    () => new BufferService("Countries", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  const svcPlaceTypes = useMemo(
    () => new BufferService("PlaceTypes", ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  /* =====================================================================================================================
   * Operation normalization
   *
   * normalizedOperation provides a stable lower-case mode value used by:
   * - DynamicForm mode
   * - map mode and interactivity
   * - submit logic
   * =================================================================================================================== */

  const normalizedOperation = useMemo(() => {
    const raw = String(operation || "").trim();
    const low = raw.toLowerCase();
    if (low === "create") return "create";
    if (low === "edit") return "edit";
    if (low === "update") return "edit";
    if (low === "view") return "view";
    if (raw === "Create") return "create";
    if (raw === "Edit") return "edit";
    if (raw === "View") return "view";
    return low || "view";
  }, [operation]);

  /* View mode disables editing and map picking. */
  const isViewMode = normalizedOperation === "view";

  /* Map mode maps CRUD mode into EsriGenericMap expected values. */
  const mapMode = useMemo(() => {
    if (normalizedOperation === "create") return "create";
    if (normalizedOperation === "edit") return "update";
    return "view";
  }, [normalizedOperation]);

  /* Map locale adapts Esri UI locale to the active i18n language. */
  const mapLocale = useMemo(() => {
    const lng = String(i18n.language || "pt").toLowerCase();
    if (lng.startsWith("pt")) return "pt-PT";
    if (lng.startsWith("es")) return "es-ES";
    return "en-US";
  }, [i18n.language]);

  /* =====================================================================================================================
   * Bootstrap loader gate
   *
   * bootstrapPendingRef indicates that initial data loading is in progress.
   * ctx.setIsLoad is cleared after transitions settle to avoid loader flicker.
   * =================================================================================================================== */

  const bootstrapPendingRef = useRef(false);
  const [bootstrapTick, setBootstrapTick] = useState(0);
  const bumpBootstrapTick = useCallback(() => setBootstrapTick((x) => x + 1), []);

  useEffect(() => {
    if (!bootstrapPendingRef.current) return;
    if (!isPendingUi) {
      bootstrapPendingRef.current = false;
      ctx.setIsLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingUi, bootstrapTick]);

  /* =====================================================================================================================
   * Tab title updater
   *
   * Updates the tab title based on the current mode and entity name.
   * =================================================================================================================== */

  useEffect(() => {
    if (!selectedTab || !operation) return;

    const key =
      normalizedOperation === "create"
        ? "LabelNewLocation"
        : normalizedOperation === "edit"
          ? "LabelEditLocation"
          : "LabelViewLocation";

    const name = sessionLine?.Name ?? locationInfo?.Name ?? "";
    const base = t(key);
    const label =
      normalizedOperation === "create" || !name ? base : `${base} "${name}"`;

    renameTab(String(selectedTab), label);
  }, [
    selectedTab,
    operation,
    normalizedOperation,
    sessionLine?.Name,
    locationInfo?.Name,
    renameTab,
    t,
  ]);

  /* =====================================================================================================================
   * Form patch application
   *
   * applyPatchToForm applies a partial patch to:
   * - local locationInfo state
   * - DynamicForm's internal state via reset (preserving dirty/touched/errors)
   *
   * Coordinates are normalized to finite numbers rounded to 6 decimals.
   * =================================================================================================================== */

  /**
   * Applies a patch to local state and synchronizes the DynamicForm values.
   * @param {object} patch - Partial field updates.
   */
  const applyPatchToForm = useCallback(
    (patch) => {
      if (!patch || typeof patch !== "object") return;

      const normalized = { ...patch };
      if (normalized.Latitude != null) {
        const n = toNumberOrNull(normalized.Latitude);
        normalized.Latitude = n == null ? null : round6(n);
      }
      if (normalized.Longitude != null) {
        const n = toNumberOrNull(normalized.Longitude);
        normalized.Longitude = n == null ? null : round6(n);
      }

      /* Avoid state updates when the patch does not introduce changes. */
      if (shallowEqualPatch(locationInfoRef.current, normalized)) return;

      /* Update local state within a transition to keep UI responsive. */
      startUiTransition(() => {
        setLocationInfo((prev) => ({ ...prev, ...normalized }));
      });

      const api = formRef.current;
      if (api?.getValues && api?.reset) {
        const current = api.getValues() || {};
        const next = { ...current, ...normalized };

        /* Skip form reset when computed values are identical to current values. */
        let changed = false;
        for (const k of Object.keys(normalized)) {
          if (current?.[k] !== next?.[k]) {
            changed = true;
            break;
          }
        }
        if (!changed) return;

        /* Reset keeps existing form interaction state while applying new values. */
        startUiTransition(() => {
          api.reset(next, {
            keepDirty: true,
            keepTouched: true,
            keepErrors: true,
          });
        });
      }
    },
    [startUiTransition]
  );

  /* =====================================================================================================================
   * Patch batching
   *
   * enqueuePatchToForm batches multiple patch requests into a single microtask flush.
   * This reduces repeated renders and form resets during rapid updates (map clicks, reverse geocode, typing).
   *
   * Additional refs track a flow where CountryId is resolved asynchronously after map selection.
   * =================================================================================================================== */

  const pendingPatchRef = useRef(null);
  const patchFlushScheduledRef = useRef(false);

  const pickCountryActiveRef = useRef(false);
  const pickCountryInflightRef = useRef(0);
  const pickCountryPatchedRef = useRef(false);

  /**
   * Flushes any accumulated patch into a single applyPatchToForm call.
   */
  const flushPendingPatch = useCallback(() => {
    patchFlushScheduledRef.current = false;

    const patch = pendingPatchRef.current;
    pendingPatchRef.current = null;
    if (!patch) return;

    /* Track when CountryId has been applied, allowing the loader to be cleared later. */
    if (Object.prototype.hasOwnProperty.call(patch, "CountryId")) {
      pickCountryPatchedRef.current = true;
      bumpLoadingTick();
    }

    applyPatchToForm(patch);
  }, [applyPatchToForm, bumpLoadingTick]);

  /**
   * Adds a patch to the pending batch and schedules a microtask flush.
   * @param {object} patch - Partial field updates.
   */
  const enqueuePatchToForm = useCallback(
    (patch) => {
      if (!patch || typeof patch !== "object") return;

      pendingPatchRef.current = { ...(pendingPatchRef.current || {}), ...patch };

      if (patchFlushScheduledRef.current) return;
      patchFlushScheduledRef.current = true;

      Promise.resolve().then(() => {
        flushPendingPatch();
      });
    },
    [flushPendingPatch]
  );

  /* =====================================================================================================================
   * FK query seeding
   *
   * fkInitialQueries stores initial search strings for foreign key fields so the UI can pre-filter options.
   * =================================================================================================================== */

  /**
   * Updates the initial query string for a foreign key field in a safe, transition-based way.
   * @param {string} field - Foreign key field name.
   * @param {unknown} value - Query value to store.
   */
  const setFkInitialQuerySafe = useCallback(
    (field, value) => {
      const f = String(field || "").trim();
      if (!f) return;
      const v = String(value ?? "");

      startUiTransition(() => {
        setFkInitialQueries((prev) => {
          const cur = (prev || {})[f] ?? "";
          if (String(cur) === v) return prev;
          return { ...(prev || {}), [f]: v };
        });
      });
    },
    [startUiTransition]
  );

  /* =====================================================================================================================
   * Derived loader state for the map selection flow
   *
   * derivedMapPickLoading is true while:
   * - a map pick flow is active, and
   * - CountryId has not been patched yet, or
   * - there are inflight country resolution requests, or
   * - the UI is still pending a transition
   * =================================================================================================================== */

  const derivedMapPickLoading = useMemo(() => {
    const active = pickCountryActiveRef.current;
    if (!active) return false;

    const inflight = pickCountryInflightRef.current;
    const patched = pickCountryPatchedRef.current;

    return !patched || inflight > 0 || isPendingUi;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPendingUi, loadingTick]);

  useEffect(() => {
    if (!pickCountryActiveRef.current) return;

    ctx.setIsLoad(!!derivedMapPickLoading);

    /* Reset the flow flags once the derived loader state becomes false. */
    if (!derivedMapPickLoading) {
      pickCountryActiveRef.current = false;
      pickCountryInflightRef.current = 0;
      pickCountryPatchedRef.current = false;
    }
  }, [ctx, derivedMapPickLoading]);

  /* =====================================================================================================================
   * Boot and restore
   *
   * On mount, the component restores per-tab session values (if available),
   * normalizes operation/line inputs, and triggers bootstrap loading.
   * =================================================================================================================== */

  useEffect(() => {
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

    const snapInfo = pick("locationInfo");
    const snapIF = pick("isFirst");
    const snapOP = pick("operation");
    const snapSL = pick("sessionLine");
    const snapOptions = pick("allOptions") ?? pick("formSchema");
    const snapId = pick("locationId");
    const snapFk = pick("fkInitialQueries");

    if (snapInfo) setLocationInfo(snapInfo);

    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    const effectiveOperation = seedOperation ?? operation;
    const effectiveLine = seedLine ?? sessionLine;

    const effectiveId = deriveId(
      effectiveLine,
      snapInfo ?? locationInfo,
      locationId,
      snapId
    );

    setOperation(effectiveOperation);
    setSessionLine(effectiveLine);
    setLocationId(effectiveId);

    if (Array.isArray(snapOptions)) setAllOptions(snapOptions);
    if (snapFk && typeof snapFk === "object") setFkInitialQueries(snapFk);

    /* Consume one-time global navigation values to prevent reapplying them. */
    if (globalOperation !== undefined) {
      dispatch(M_StateActions.removeM_State({ key: "operation" }));
    }
    if (globalLine !== undefined) {
      dispatch(M_StateActions.removeM_State({ key: "LocationLine" }));
    }

    bootstrap(effectiveOperation, effectiveLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Performs the initial data loading for the current tab session:
   * - loads form schema/options
   * - loads entity details when applicable
   * @param {string} op - Operation mode.
   * @param {object} line - Context line containing entity Id when applicable.
   */
  const bootstrap = async (op, line) => {
    const token = tabTokenRef.current;

    bootstrapPendingRef.current = true;
    ctx.setIsLoad(true);

    try {
      await fetchOptions();

      const isCreate = String(op || "").toLowerCase() === "create";
      const entityId = line?.Id || sessionLineRef.current?.Id;

      if (!isCreate && entityId) {
        await fetchDetails(entityId, token);
      }
    } finally {
      bumpBootstrapTick();
    }
  };

  /* =====================================================================================================================
   * Per-tab snapshot persistence
   *
   * Persists a snapshot of the current tab state to Redux with a short debounce.
   * This enables restoring the form/map state when switching between tabs.
   * =================================================================================================================== */

  const saveSnapshotTimer = useRef(null);

  useEffect(() => {
    if (!id) return;

    /* In non-view modes, merge current form values into persisted info to keep draft changes. */
    const currentFormValues = formRef.current?.getValues?.() || {};
    const infoToPersist = isViewMode
      ? locationInfo
      : { ...locationInfo, ...currentFormValues };

    const sessionData = [
      { key: "locationInfo", value: infoToPersist },
      { key: "isFirst", value: isFirst },
      { key: "operation", value: operation },
      { key: "sessionLine", value: sessionLine },
      { key: "allOptions", value: allOptions },
      { key: "locationId", value: locationId },
      { key: "fkInitialQueries", value: fkInitialQueries },
    ];

    if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);

    saveSnapshotTimer.current = setTimeout(() => {
      dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, 80);

    return () => {
      if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    };
  }, [
    id,
    locationInfo,
    isFirst,
    operation,
    sessionLine,
    allOptions,
    locationId,
    fkInitialQueries,
    dispatch,
    isViewMode,
  ]);

  /* =====================================================================================================================
   * Tab switching
   *
   * When selectedTab changes, restores the session snapshot for the new tab id.
   * If no snapshot exists, performs bootstrap loading for that tab.
   * =================================================================================================================== */

  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === "selectedTab")?.value;
    if (newId && newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  /**
   * Restores session state for a given tab id, or loads it from the backend when missing.
   * @param {string} sessionID - Tab id to restore.
   */
  const getSessionStorageData = (sessionID) => {
    bootstrapPendingRef.current = true;
    ctx.setIsLoad(true);

    const data = M_State.find((obj) => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;

      const tempLI = pick("locationInfo");
      const tempIF = pick("isFirst");
      const tempOP = pick("operation");
      const tempSL = pick("sessionLine");
      const tempOptions = pick("allOptions") ?? pick("formSchema");
      const tempId = pick("locationId");
      const tempFk = pick("fkInitialQueries");

      startUiTransition(() => {
        setLocationInfo(tempLI || {});
        setIsFirst(tempIF ?? false);
        setOperation(tempOP ?? "Error");
        setSessionLine(tempSL || {});
        setLocationId(deriveId(tempSL, tempLI, locationIdRef.current, tempId));

        if (Array.isArray(tempOptions)) setAllOptions(tempOptions);
        if (tempFk && typeof tempFk === "object") setFkInitialQueries(tempFk);
      });

      bumpBootstrapTick();
      return;
    }

    bootstrap(operationRef.current, sessionLineRef.current).finally(() => {
      bumpBootstrapTick();
    });
  };

  /* =====================================================================================================================
   * i18n changes
   *
   * When language changes after the first load, re-fetches schema/options so labels are updated.
   * =================================================================================================================== */

  useEffect(() => {
    if (!operation || isFirst) return;
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  /* =====================================================================================================================
   * Data fetching
   *
   * fetchOptions loads the dynamic schema/options for the Locations form.
   * fetchDetails loads an existing Location entity by Id and resets auto-fill tracking flags.
   * =================================================================================================================== */

  /**
   * Loads the schema/options for the Locations dynamic form.
   */
  const fetchOptions = async () => {
    const myToken = tabTokenRef.current;
    try {
      const res = await svcLocations.options();

      if (isFailurePayload(res) || res?.name === "AxiosError") {
        toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
        return;
      }

      /* Ignore late results for a different tab session. */
      if (myToken !== tabTokenRef.current) return;

      startUiTransition(() => {
        setAllOptions(Array.isArray(res) ? res : []);
      });
    } catch (err) {
      toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
    }
  };

  /**
   * Loads entity details for an existing Location and initializes form/session identifiers.
   * @param {number | string} idToGet - Location Id to load.
   * @param {number} _token - Captured token from the caller (unused but kept for call symmetry).
   */
  const fetchDetails = async (idToGet, _token) => {
    const myToken = tabTokenRef.current;
    try {
      const res = await svcLocations.getId(idToGet);

      if (isFailurePayload(res)) {
        toastMessages("error", "", apiMessage(res, t("ErrorLoadingData")));
        return;
      }

      /* Ignore late results for a different tab session. */
      if (myToken !== tabTokenRef.current) return;

      startUiTransition(() => {
        setLocationInfo(res || {});
        setLocationId(res?.Id);
        setSessionLine((prev) =>
          prev && prev.Id ? prev : { ...prev, Id: res?.Id, Name: res?.Name }
        );
      });

      /* Reset auto-fill tracking so a new reverse-geocode session behaves predictably. */
      lastCoordsForAutoRef.current = null;
      userEditedNameRef.current = false;
      userEditedAddressRef.current = false;
      userEditedCodeRef.current = false;
      userEditedPlaceTypeRef.current = false;
      userEditedCountryRef.current = false;
      forceOverwriteNextAutoRef.current = false;
      lastAutoAddressRef.current = "";
    } catch (err) {
      toastMessages("error", "", apiMessage(err, t("ErrorLoadingData")));
    }
  };

  /* =====================================================================================================================
   * Payload building
   *
   * buildPayloadFromValues maps DynamicForm values into an API payload:
   * - Uses schema/options to decide which keys to include
   * - Applies boolean and foreign key conversions
   * - Normalizes coordinate fields
   * =================================================================================================================== */

  /**
   * Builds an API payload from DynamicForm values, using the dynamic schema/options.
   * @param {object} values - DynamicForm values object.
   * @returns {object} API payload object.
   */
  const buildPayloadFromValues = (values) => {
    const options = Array.isArray(allOptions) ? allOptions : [];
    const entries = [];

    for (const opt of options) {
      if (!opt) continue;

      /* Foreign key fields may use Header as the form value source. */
      const keySource = opt.IsForeignKey ? opt.Header || opt.Name : opt.Name;
      const payloadKey = toCamel(opt.Header || keySource);

      let v;

      /* Normalize booleans as explicit true/false. */
      if (isBoolean(opt)) {
        v = !!values?.[opt.Name];
      } else if (opt.IsForeignKey) {
        /* Normalize foreign key values to numbers or null. */
        const rawId = values?.[keySource];
        v = rawId == null || rawId === "" ? null : Number(rawId);
      } else {
        v = values?.[keySource];
      }

      /* Normalize coordinate fields to finite numbers with 6 decimal digits. */
      if (payloadKey === "latitude") {
        const n = toNumberOrNull(v);
        v = n == null ? null : round6(n);
      }
      if (payloadKey === "longitude") {
        const n = toNumberOrNull(v);
        v = n == null ? null : round6(n);
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  };

  /* =====================================================================================================================
   * Submit and refresh
   *
   * onSubmitDynamic validates required fields and calls create/update endpoints.
   * onRefresh reloads schema and, when applicable, reloads entity details.
   * =================================================================================================================== */

  /**
   * Handles DynamicForm submit for create and edit operations.
   * @param {object} values - DynamicForm values.
   */
  const onSubmitDynamic = useCallback(
    async (values) => {
      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);

        /* Required fields validation for the Location entity. */
        if (!payload.code || !payload.name || !payload.placeTypeId || !payload.countryId) {
          toastMessages("error", "", t("MandatoryFields"));
          return;
        }

        const isCreate = normalizedOperation === "create";
        const lid =
          locationIdRef.current ??
          deriveId(sessionLineRef.current, locationInfoRef.current, null, null);

        /* Edit mode requires a resolvable Id. */
        if (!isCreate && !lid) {
          toastMessages("error", "", t("ErrorSaving"));
          return;
        }

        const res = isCreate ? await svcCreate.post(payload) : await svcUpdate.update(lid, payload);

        if (isFailurePayload(res)) {
          toastMessages("error", "", apiMessage(res, t("ErrorSaving")));
          return;
        }

        toastMessages("success", "", isCreate ? t("LocationCreated") : t("LocationUpdated"));
        handleTabClose(idRef.current);
      } catch (err) {
        toastMessages("error", "", apiMessage(err, t("ErrorSaving")));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx, handleTabClose, normalizedOperation, svcCreate, svcUpdate, t, toastMessages, allOptions]
  );

  /* Triggers DynamicForm submit via the imperative ref API. */
  const triggerFormSubmit = () => formRef.current?.submit?.();

  /**
   * Reloads schema/options and entity details for the current session.
   */
  const onRefresh = () => {
    const effectiveLine = sessionLineRef.current;

    bootstrapPendingRef.current = true;
    ctx.setIsLoad(true);

    Promise.all([
      fetchOptions(),
      normalizedOperation !== "create" && effectiveLine?.Id
        ? fetchDetails(effectiveLine.Id, tabTokenRef.current)
        : Promise.resolve(),
    ]).finally(() => {
      bumpBootstrapTick();
    });
  };

  /* =====================================================================================================================
   * Form change handling
   *
   * handleFormValuesChange receives patches from DynamicForm.
   * - For FK query events, it updates fkInitialQueries
   * - For field edits, it updates userEdited flags to protect auto-fill
   * - For coordinate edits, it normalizes values before storing
   * =================================================================================================================== */

  /**
   * Handles value changes emitted by DynamicForm.
   * @param {object} patch - Partial field updates.
   * @param {object} meta - Change metadata provided by the form.
   */
  const handleFormValuesChange = useCallback(
    (patch, meta) => {
      /* Foreign key dropdowns can emit query changes without changing the selected value. */
      if (meta?.cause === "fk-query" && meta?.name) {
        const fieldName = String(meta.name);
        const nextQ = String(meta.query ?? "");
        setFkInitialQuerySafe(fieldName, nextQ);
        return;
      }

      if (!patch) return;

      /* Track manual edits so auto-fill does not overwrite user intent. */
      if (Object.prototype.hasOwnProperty.call(patch, "Name")) {
        const v = String(patch.Name ?? "").trim();
        userEditedNameRef.current = v !== String(lastAutoNameRef.current || "").trim();
      }
      if (Object.prototype.hasOwnProperty.call(patch, "Address")) {
        const v = String(patch.Address ?? "").trim();
        userEditedAddressRef.current = v !== String(lastAutoAddressRef.current || "").trim();
      }
      if (Object.prototype.hasOwnProperty.call(patch, "Code")) {
        const v = String(patch.Code ?? "").trim();
        userEditedCodeRef.current = v !== String(lastAutoCodeRef.current || "").trim();
      }
      if (Object.prototype.hasOwnProperty.call(patch, "CountryId")) {
        const v = toNumberOrNull(patch.CountryId);
        userEditedCountryRef.current = v !== lastAutoCountryIdRef.current;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "PlaceTypeId")) {
        const v = toNumberOrNull(patch.PlaceTypeId);
        userEditedPlaceTypeRef.current = v !== lastAutoPlaceTypeIdRef.current;
      }

      const normalized = { ...patch };
      if (normalized.Latitude != null) {
        const n = toNumberOrNull(normalized.Latitude);
        normalized.Latitude = n == null ? null : round6(n);
      }
      if (normalized.Longitude != null) {
        const n = toNumberOrNull(normalized.Longitude);
        normalized.Longitude = n == null ? null : round6(n);
      }

      /* Keep locationInfo in sync with form changes. */
      if (!shallowEqualPatch(locationInfoRef.current, normalized)) {
        setLocationInfo((prev) => ({ ...prev, ...normalized }));
      }
    },
    [setFkInitialQuerySafe]
  );

  /* =====================================================================================================================
   * Map configuration
   *
   * The map displays a marker when coordinates exist and drives viewpoint through props.
   * The marker layer object is kept stable to minimize layer re-creation and map churn.
   * =================================================================================================================== */

  /* Map widgets enabled in the EsriGenericMap instance. */
  const mapWidgets = useMemo(() => ({ search: { position: "top-right" } }), []);

  /* Default center used when no coordinates are selected (longitude, latitude). */
  const defaultMapCenter = useMemo(() => [-9.14, 38.72], []);

  /* Initial zoom used when no coordinates are selected. */
  const initialZoom = 4;

  /* Map constraints control allowed zoom range. */
  const mapConstraints = useMemo(() => ({ minZoom: 2 }), []);

  /* Stores the map component/element reference provided by EsriGenericMap. */
  const mapComponentRef = useRef(null);

  /**
   * Computes the current picked coordinates from locationInfo and normalizes them.
   * Returns null when coordinates are missing or effectively zero.
   */
  const pickedCoords = useMemo(() => {
    const lat = toNumberOrNull(locationInfo?.Latitude);
    const lon = toNumberOrNull(locationInfo?.Longitude);

    if (lat == null || lon == null) return null;
    if (!isFiniteNumber(lat) || !isFiniteNumber(lon)) return null;

    const rLat = round6(lat);
    const rLon = round6(lon);

    /* Treat near-zero coordinates as "not selected". */
    if (Math.abs(rLat) < 0.000001 && Math.abs(rLon) < 0.000001) return null;

    return { lat: rLat, lon: rLon };
  }, [locationInfo?.Latitude, locationInfo?.Longitude]);

  /* =====================================================================================================================
   * Marker layer management
   *
   * markerLayerRef holds a stable layer object reference.
   * The layer's geometry and properties are mutated in-place when coordinates or labels change.
   * =================================================================================================================== */

  const markerLayerRef = useRef(null);

  /* Build the marker layer only once to preserve object identity across renders. */
  if (!markerLayerRef.current) {
    markerLayerRef.current = {
      title: "Picked Location",
      type: "geojson",
      geojson: {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: [0, 0] },
            properties: {
              id: "picked",
              sig: "",
              name: "",
              code: "",
              countryId: null,
              lon: 0,
              lat: 0,
              Longitude: 0,
              Latitude: 0,
              x: 0,
              y: 0,
            },
          },
        ],
      },
      symbol: {
        type: "simple-marker",
        style: "path",
        path: PIN_PATH,
        size: 26,
        color: PIN_FILL,
        outline: { type: "simple-line", width: 1.8, color: PIN_OUTLINE },
      },
    };
  }

  /* Update marker geometry and properties when coordinates or key labels change. */
  useEffect(() => {
    const layer = markerLayerRef.current;
    if (!layer) return;

    const f0 = layer.geojson?.features?.[0];
    if (!f0) return;

    /* When no coordinates exist, place the marker at 0,0 and clear identification properties. */
    if (!pickedCoords) {
      f0.geometry.coordinates[0] = 0;
      f0.geometry.coordinates[1] = 0;

      f0.properties.sig = "";
      f0.properties.name = "";
      f0.properties.code = "";
      f0.properties.countryId = null;
      return;
    }

    const lon = pickedCoords.lon;
    const lat = pickedCoords.lat;

    /* Update geometry in-place. */
    f0.geometry.coordinates[0] = lon;
    f0.geometry.coordinates[1] = lat;

    /* Update properties in-place for downstream consumers and debugging. */
    const name = String(locationInfo?.Name || "");
    const code = String(locationInfo?.Code || "");
    const countryId = toNumberOrNull(locationInfo?.CountryId);

    f0.properties.sig = `picked:${lon},${lat}`;
    f0.properties.name = name;
    f0.properties.code = code;
    f0.properties.countryId = countryId;

    f0.properties.lon = lon;
    f0.properties.lat = lat;
    f0.properties.Longitude = lon;
    f0.properties.Latitude = lat;
    f0.properties.x = lon;
    f0.properties.y = lat;
  }, [pickedCoords, locationInfo?.Name, locationInfo?.Code, locationInfo?.CountryId]);

  /**
   * Exposes the layers array to the map.
   * Returns an empty array when no coordinates exist to avoid displaying a marker.
   */
  const mapLayers = useMemo(() => {
    if (!pickedCoords) return [];
    return [markerLayerRef.current];
  }, [pickedCoords]);

  /* =====================================================================================================================
   * Viewpoint control
   *
   * debouncedCoords introduces a small delay so frequent coordinate updates do not cause rapid viewpoint changes.
   * effectiveCenter/effectiveZoom are derived props passed into the map for consistent updates.
   * =================================================================================================================== */

  const [debouncedCoords, setDebouncedCoords] = useState(null);

  useEffect(() => {
    const tm = setTimeout(() => {
      setDebouncedCoords(pickedCoords || null);
    }, 220);

    return () => clearTimeout(tm);
  }, [pickedCoords]);

  const effectiveCenter = useMemo(() => {
    if (!debouncedCoords) return defaultMapCenter;
    return [debouncedCoords.lon, debouncedCoords.lat];
  }, [debouncedCoords, defaultMapCenter]);

  const effectiveZoom = useMemo(() => {
    if (!debouncedCoords) return initialZoom;
    return isViewMode ? 14 : 8;
  }, [debouncedCoords, initialZoom, isViewMode]);

  /**
   * Captures the map component reference when EsriGenericMap signals readiness.
   * @param {unknown} componentOrEl - Component instance or element reference provided by EsriGenericMap.
   */
  const handleMapReady = useCallback((componentOrEl) => {
    mapComponentRef.current = componentOrEl;
  }, []);

  /* Debug logging for map state transitions. */
  useEffect(() => {
    //console.log("pickedCoords", pickedCoords);
  }, [pickedCoords]);

  /* Debug logging for layer coordinate changes. */
  useEffect(() => {
    //console.log("mapLayers changed", mapLayers?.[0]?.geojson?.features?.[0]?.geometry?.coordinates);
  }, [mapLayers]);

  /* =====================================================================================================================
   * Reverse geocode extractors
   *
   * These helpers extract values from the reverse-geocode candidate object using multiple fallbacks.
   * The candidate structure can vary depending on the reverse geocode provider and configuration.
   * =================================================================================================================== */

  /**
   * Extracts a country name from a reverse-geocode candidate.
   * @param {object} candidate - Reverse-geocode candidate.
   * @returns {string} Country name or empty string.
   */
  const extractCountryName = useCallback((candidate) => {
    if (!candidate) return "";
    const a = candidate.attributes || candidate.Attributes || {};
    const addr = candidate.address || candidate.Address || {};

    const direct = pickFirstString(
      a.Country,
      a.COUNTRY,
      a.CountryName,
      a.CNTRYNAME,
      addr.Country,
      addr.country,
      candidate.country,
      candidate.Country
    );
    if (direct) return direct;

    const longLabel = pickFirstString(
      a.LongLabel,
      a.longLabel,
      a.Match_addr,
      a.match_addr,
      candidate.label
    );

    /* When labels contain comma-separated segments, the last segment commonly represents a country. */
    if (longLabel.includes(",")) {
      const parts = longLabel
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);

      const last = parts[parts.length - 1];
      if (last && !/^\d+$/.test(last)) return last;
    }

    return "";
  }, []);

  /**
   * Extracts a place label/name from a reverse-geocode candidate.
   * @param {object} candidate - Reverse-geocode candidate.
   * @returns {string} Place label or empty string.
   */
  const extractPlaceLabel = useCallback((candidate) => {
    if (!candidate) return "";
    const a = candidate.attributes || candidate.Attributes || {};
    const addr = candidate.address || candidate.Address || {};

    return pickFirstString(
      candidate.name,
      candidate.label,
      a.PlaceName,
      a.placeName,
      a.ShortLabel,
      a.shortLabel,
      a.LongLabel,
      a.longLabel,
      a.Match_addr,
      a.match_addr,
      addr.Address,
      addr.address
    );
  }, []);

  /**
   * Extracts an address label from a reverse-geocode candidate.
   * @param {object} candidate - Reverse-geocode candidate.
   * @returns {string} Address label or empty string.
   */
  const extractAddressLabel = useCallback((candidate) => {
    if (!candidate) return "";
    const a = candidate.attributes || candidate.Attributes || {};
    const addr = candidate.address || candidate.Address || {};

    return pickFirstString(
      addr.Address,
      addr.address,
      a.Match_addr,
      a.match_addr,
      a.LongLabel,
      a.longLabel,
      a.ShortLabel,
      a.shortLabel,
      candidate.label
    );
  }, []);

  /**
   * Extracts a country code from a reverse-geocode candidate.
   * @param {object} candidate - Reverse-geocode candidate.
   * @returns {string} Country code or empty string.
   */
  const extractCountryCode = useCallback((candidate) => {
    if (!candidate) return "";
    const a = candidate.attributes || candidate.Attributes || {};
    const addr = candidate.address || candidate.Address || {};

    return pickFirstString(
      a.CountryCode,
      a.countryCode,
      a.CNTRYCD,
      a.CNTRYCODE,
      addr.CountryCode,
      addr.countrycode,
      candidate.countryCode,
      candidate.CountryCode
    );
  }, []);

  /* =====================================================================================================================
   * Country resolution and clearing
   *
   * CountryId is determined by reverse geocoding and then resolved against the Countries endpoint.
   * When a match is not found, CountryId is cleared while still seeding the FK initial query.
   * =================================================================================================================== */

  /**
   * Seeds the CountryId FK query and clears CountryId when no reliable match exists.
   * @param {string} countryHint - Country name or code used as a query hint.
   */
  const clearCountryForUnknown = useCallback(
    (countryHint) => {
      const q = String(countryHint || "").trim();
      setFkInitialQuerySafe("CountryId", q);

      /* Do not clear user-selected values. */
      if (userEditedCountryRef.current) return;

      lastAutoCountryIdRef.current = null;
      enqueuePatchToForm({ CountryId: null });
    },
    [enqueuePatchToForm, setFkInitialQuerySafe]
  );

  /* Sequence counter used to ignore out-of-order reverse geocode resolutions. */
  const reverseGeocodeSeqRef = useRef(0);

  /**
   * Seeds the country FK query and attempts to resolve an Id by querying the Countries endpoint.
   * @param {string} countryName - Candidate country name.
   * @param {string} countryCode - Candidate country code.
   * @param {object} opts - Control options.
   * @param {boolean} opts.allowOverwrite - Whether auto-fill is allowed to set CountryId.
   * @param {number} opts.seq - Sequence number used to ignore outdated results.
   */
  const seedCountryQueryAndMaybeResolveId = useCallback(
    async (countryName, countryCode, { allowOverwrite, seq }) => {
      const name = String(countryName || "").trim();
      const code = String(countryCode || "").trim();

      const seed = name || code;
      if (!seed) return;

      /* Seed FK query so the dropdown can surface relevant options. */
      setFkInitialQuerySafe("CountryId", seed);

      /* Avoid overwriting user input or running unnecessary lookups. */
      if (!allowOverwrite) return;
      if (userEditedCountryRef.current) return;

      pickCountryInflightRef.current += 1;
      bumpLoadingTick();

      try {
        const safeName = name.replace(/'/g, "''");
        const safeCode = code.replace(/'/g, "''");

        /* Try matching by code first, then by name, using alternative casing conventions. */
        const tryFilters = [];
        if (code) tryFilters.push(`Code eq '${safeCode}'`, `code eq '${safeCode}'`);
        if (name) tryFilters.push(`Name eq '${safeName}'`, `name eq '${safeName}'`);

        for (const filterExpr of tryFilters) {
          /* Stop if a newer reverse geocode request has started. */
          if (seq !== reverseGeocodeSeqRef.current) return;

          const qs = `?Page=1&PageSize=10&Filter=${encodeURIComponent(filterExpr)}`;
          const res = await svcCountries.filter(qs);

          if (seq !== reverseGeocodeSeqRef.current) return;

          const items = Array.isArray(res?.Items)
            ? res.Items
            : Array.isArray(res)
              ? res
              : [];

          if (items.length >= 1) {
            const idNum = Number(items[0]?.Id);
            if (Number.isFinite(idNum) && idNum > 0) {
              lastAutoCountryIdRef.current = idNum;
              userEditedCountryRef.current = false;
              enqueuePatchToForm({ CountryId: idNum });
              return;
            }
          }
        }

        clearCountryForUnknown(seed);
      } catch {
        clearCountryForUnknown(seed);
      } finally {
        pickCountryInflightRef.current = Math.max(0, pickCountryInflightRef.current - 1);
        bumpLoadingTick();
      }
    },
    [svcCountries, clearCountryForUnknown, enqueuePatchToForm, setFkInitialQuerySafe, bumpLoadingTick]
  );

  /* =====================================================================================================================
   * PlaceType caching and inference
   *
   * Place types are loaded once and cached in memory.
   * A simple inference uses reverse-geocode labels to choose between "Port" and "Location".
   * =================================================================================================================== */

  const placeTypeMapRef = useRef(null);
  const placeTypeLoadPromiseRef = useRef(null);

  /**
   * Ensures the place type lookup map is loaded and cached.
   * @returns {Promise<object>} Map from lower-cased place type name to numeric Id.
   */
  const ensurePlaceTypeMap = useCallback(async () => {
    if (placeTypeMapRef.current) return placeTypeMapRef.current;
    if (placeTypeLoadPromiseRef.current) return placeTypeLoadPromiseRef.current;

    placeTypeLoadPromiseRef.current = (async () => {
      try {
        const res =
          (await svcPlaceTypes.get?.()) ??
          (await svcPlaceTypes.getAll?.()) ??
          (await svcPlaceTypes.filter?.(""));

        const items = Array.isArray(res?.Items) ? res.Items : Array.isArray(res) ? res : [];
        const map = Object.create(null);

        for (const it of items) {
          const name = String(it?.Name || "").trim().toLowerCase();
          const idNum = Number(it?.Id);
          if (name && Number.isFinite(idNum) && idNum > 0) map[name] = idNum;
        }

        placeTypeMapRef.current = map;
        return map;
      } catch {
        placeTypeMapRef.current = Object.create(null);
        return placeTypeMapRef.current;
      }
    })();

    return placeTypeLoadPromiseRef.current;
  }, [svcPlaceTypes]);

  /**
   * Seeds PlaceTypeId query and resolves the Id based on inferred type name.
   * @param {object} candidate - Reverse-geocode candidate.
   * @param {object} opts - Control options.
   * @param {boolean} opts.allowOverwrite - Whether auto-fill is allowed to set PlaceTypeId.
   */
  const seedPlaceTypeAndMaybeResolveId = useCallback(
    async (candidate, { allowOverwrite }) => {
      if (!allowOverwrite) return;
      if (userEditedPlaceTypeRef.current) return;

      const label = String(extractPlaceLabel(candidate) || "").toLowerCase();
      const a = candidate?.attributes || candidate?.Attributes || {};
      const typeish = String(a.Addr_type || a.Type || a.PlaceType || a.Category || "").toLowerCase();

      /* Heuristic: match common port terms in multiple languages to infer "Port" vs "Location". */
      const looksPort =
        /\b(port|harbor|harbour|seaport|dock|pier|quay|wharf|marina|terminal)\b/.test(label) ||
        /\b(port|harbor|harbour|seaport|dock|pier|quay|wharf|marina|terminal)\b/.test(typeish) ||
        /\bporto\b/.test(label) ||
        /\bporto de\b/.test(label) ||
        /\bdoca\b/.test(label) ||
        /\bcais\b/.test(label) ||
        /\bmarina\b/.test(label) ||
        /\bterminal\b/.test(label) ||
        /\bpuerto\b/.test(label) ||
        /\bmuelle\b/.test(label) ||
        /\bdarsena\b/.test(label) ||
        /\bdársena\b/.test(label);

      const targetName = looksPort ? "Port" : "Location";

      /* Seed FK query so the dropdown can show relevant options. */
      setFkInitialQuerySafe("PlaceTypeId", targetName);

      const map = await ensurePlaceTypeMap();
      const idNum = map[String(targetName).toLowerCase()];

      if (Number.isFinite(idNum) && idNum > 0) {
        const current = toNumberOrNull(locationInfoRef.current?.PlaceTypeId);

        /* Only set PlaceTypeId when it is currently empty and user has not edited it. */
        if (!current) {
          lastAutoPlaceTypeIdRef.current = idNum;
          userEditedPlaceTypeRef.current = false;
          enqueuePatchToForm({ PlaceTypeId: idNum });
        }
      }
    },
    [enqueuePatchToForm, extractPlaceLabel, ensurePlaceTypeMap, setFkInitialQuerySafe]
  );

  /* =====================================================================================================================
   * Map click and reverse geocode handlers
   *
   * handleMapClick applies coordinates into the form and activates the country resolution flow.
   * handleReverseGeocode processes reverse-geocode candidates to auto-fill fields while respecting user edits.
   * =================================================================================================================== */

  /**
   * Handles map click events and writes Latitude/Longitude into the form state.
   * @param {object} param0 - Click payload.
   * @param {number|string} param0.lat - Latitude.
   * @param {number|string} param0.lon - Longitude.
   */
  const handleMapClick = useCallback(
    ({ lat, lon }) => {
      if (isViewMode) return;

      const nLat = toNumberOrNull(lat);
      const nLon = toNumberOrNull(lon);
      if (!isFiniteNumber(nLat) || !isFiniteNumber(nLon)) return;

      /* Activate the country resolution flow for the next reverse geocode cycle. */
      pickCountryActiveRef.current = true;
      pickCountryPatchedRef.current = false;
      pickCountryInflightRef.current = 0;
      bumpLoadingTick();
      ctx.setIsLoad(true);

      /* Force the next reverse geocode to apply auto-fill for this new coordinate selection. */
      forceOverwriteNextAutoRef.current = true;
      lastCoordsForAutoRef.current = null;

      enqueuePatchToForm({
        Latitude: round6(nLat),
        Longitude: round6(nLon),
      });
    },
    [enqueuePatchToForm, isViewMode, bumpLoadingTick, ctx]
  );

  const rgDebounceTimerRef = useRef(null);
  const rgLastCandidateRef = useRef(null);

  /**
   * Handles reverse geocode candidate results from the map.
   * The handler is debounced to avoid processing intermediate candidates too frequently.
   * @param {object} candidate - Reverse-geocode candidate.
   */
  const handleReverseGeocode = useCallback(
    (candidate) => {
      if (isViewMode) return;

      rgLastCandidateRef.current = candidate;

      if (rgDebounceTimerRef.current) clearTimeout(rgDebounceTimerRef.current);

      rgDebounceTimerRef.current = setTimeout(() => {
        reverseGeocodeSeqRef.current += 1;
        const mySeq = reverseGeocodeSeqRef.current;

        const currentCoords = pickedCoords ? { lat: pickedCoords.lat, lon: pickedCoords.lon } : null;
        const lastCoords = lastCoordsForAutoRef.current;

        const coordsChanged =
          !!currentCoords &&
          (!lastCoords || lastCoords.lat !== currentCoords.lat || lastCoords.lon !== currentCoords.lon);

        const forceOverwrite = !!forceOverwriteNextAutoRef.current;
        const allowOverwrite = coordsChanged || forceOverwrite;

        /* Skip auto-fill if coordinates did not change and no overwrite has been requested. */
        if (!allowOverwrite) return;

        const c = rgLastCandidateRef.current;

        /* Resolve and seed country fields first since they affect FK selection flow. */
        const countryName = extractCountryName(c);
        const countryCode = extractCountryCode(c);

        if (countryName || countryCode) {
          seedCountryQueryAndMaybeResolveId(countryName, countryCode, {
            allowOverwrite,
            seq: mySeq,
          });
        } else {
          clearCountryForUnknown("");
        }

        const patch = {};

        /* Auto-fill Address when the user has not edited it. */
        if (!userEditedAddressRef.current) {
          const nextAddress = extractAddressLabel(c);
          if (nextAddress) {
            lastAutoAddressRef.current = String(nextAddress);
            userEditedAddressRef.current = false;
            patch.Address = nextAddress;
          }
        }

        /* Auto-fill Code when the user has not edited it. */
        if (!userEditedCodeRef.current) {
          const cc = extractCountryCode(c);

          /* Prefer a short country code when available. */
          if (cc && cc.length <= 4) {
            const next = cc.toUpperCase();
            lastAutoCodeRef.current = next;
            userEditedCodeRef.current = false;
            patch.Code = next;
          } else {
            /* Fallback: derive a compact code from the address or label. */
            const addressForCode =
              String(locationInfoRef.current?.Address || "").trim() || extractAddressLabel(c);

            const compact = String(addressForCode || "")
              .toUpperCase()
              .replace(/[^A-Z0-9 ]/g, " ")
              .trim()
              .split(/\s+/)
              .slice(0, 4)
              .map((w) => w.slice(0, 4))
              .join("");

            if (compact) {
              const next = compact.slice(0, 4);
              lastAutoCodeRef.current = next;
              userEditedCodeRef.current = false;
              patch.Code = next;
            }
          }
        }

        if (Object.keys(patch).length) enqueuePatchToForm(patch);

        /* PlaceType inference is performed after the main patch is queued. */
        seedPlaceTypeAndMaybeResolveId(c, { allowOverwrite });

        /* Clear the one-shot overwrite flag after it has been used. */
        if (forceOverwriteNextAutoRef.current) {
          forceOverwriteNextAutoRef.current = false;
        }

        /* Store the coordinates used for this auto-fill cycle. */
        if (currentCoords) lastCoordsForAutoRef.current = currentCoords;
      }, 120);
    },
    [
      isViewMode,
      pickedCoords,
      extractCountryName,
      extractCountryCode,
      extractAddressLabel,
      seedCountryQueryAndMaybeResolveId,
      seedPlaceTypeAndMaybeResolveId,
      enqueuePatchToForm,
      clearCountryForUnknown,
    ]
  );

  /* Clear reverse-geocode debounce timer on unmount. */
  useEffect(() => {
    return () => {
      if (rgDebounceTimerRef.current) clearTimeout(rgDebounceTimerRef.current);
    };
  }, []);

  /* =====================================================================================================================
   * DynamicForm key
   *
   * The key forces DynamicForm to remount when the effective entity session changes.
   * The map must not be keyed/remounted, but the form can be keyed to reflect mode/entity transitions.
   * =================================================================================================================== */

  const dynamicFormKey = useMemo(() => {
    const entityId = deriveId(sessionLine, locationInfo, locationId, null) ?? "new";
    return `df-locations-${selectedTab}-${normalizedOperation}-${entityId}`;
  }, [selectedTab, normalizedOperation, sessionLine, locationInfo, locationId]);

  /* =====================================================================================================================
   * Render
   *
   * Layout:
   * - Header actions (refresh, save)
   * - Form panel (DynamicForm)
   * - Map panel (MapPanel/EsriGenericMap)
   * =================================================================================================================== */

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t("RefreshTable")}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={onRefresh}
              label={t("RefreshTable")}
            />

            {!isViewMode && (
              <MButton
                tooltip={t("Save")}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t("Save")}
              />
            )}
          </>
        }
      />

      <div style={{ padding: "20px", background: "var(--color-white)" }}>
        <div className="firstDivContainer">
          <div className="location-config-layout">
            <div className="location-config-form">
              <DynamicForm
                key={dynamicFormKey}
                formRef={formRef}
                mode={normalizedOperation}
                schema={allOptions}
                data={locationInfo}
                onSubmit={onSubmitDynamic}
                onValuesChange={handleFormValuesChange}
                fkInitialQueries={fkInitialQueries}
                title={t("Location Information")}
                containerClassName="alignInputsUserForm"
                align="vertical"
              />
            </div>

            <div className="location-config-map">
              <MapPanel
                mode={mapMode}
                locale={mapLocale}
                widgets={mapWidgets}
                layers={mapLayers}
                center={effectiveCenter}
                zoom={effectiveZoom}
                constraints={mapConstraints}
                onComponentReady={handleMapReady}
                onMapClick={!isViewMode ? handleMapClick : undefined}
                onReverseGeocode={!isViewMode ? handleReverseGeocode : undefined}
                showLocations={false}
                locations={[]}
                enableLocationPick={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

LocationConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func,
};
